import { useCallback, useEffect, useState } from 'react';
import {
  VADE_TABAN, VADE_TABAN_LABEL, vadeEtiket, vadeGunlu, vadeSecenekHatasi,
  type VadeParca, type VadeSecenegi, type VadeTaban,
} from '@artmozaik/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { hashaKaydir } from './kaydir';

/**
 * VADE TANIMLARI — Ayarlar'daki bölüm ve teklif ekranındaki "yeni vade tanımla"
 * çekmecesi AYNI formu kullanır. Etiket ve doğrulama `@artmozaik/shared`
 * içindeki tek kuraldan gelir (sunucu da onu okur).
 */
export type VadeGorunum = VadeSecenegi & { etiket: string };
type Yanit = { secenekler: VadeGorunum[]; varsayilan: boolean };
type Taslak = { id: string | null; ad: string; parcalar: { oranPct: string; taban: VadeTaban; gun: string }[] };

const bosTaslak = (): Taslak => ({ id: null, ad: '', parcalar: [{ oranPct: '100', taban: 'SIPARIS', gun: '30' }] });
const sayi = (s: string) => Number(String(s).replace(',', '.'));

/** Addan kimlik: "Sipariş + 20 gün" → "siparis-20-gun-x7k2". Kimlik sonradan değişmez. */
function kimlikUret(ad: string): string {
  const govde = ad.toLocaleLowerCase('tr')
    .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 28);
  return `${govde || 'vade'}-${Math.random().toString(36).slice(2, 6)}`;
}

function taslaktan(t: Taslak): VadeSecenegi {
  return {
    id: t.id ?? kimlikUret(t.ad), ad: t.ad.trim(),
    parcalar: t.parcalar.map((p) => ({ oranPct: sayi(p.oranPct), taban: p.taban, gun: vadeGunlu(p.taban) ? Math.round(sayi(p.gun) || 0) : 0 })),
  };
}

export function useVadeler() {
  const [veri, setVeri] = useState<Yanit | null>(null);
  const yukle = useCallback(() => api<Yanit>('/api/vade-secenekleri').then((r) => { setVeri(r); return r; }), []);
  useEffect(() => { yukle().catch(() => undefined); }, [yukle]);
  return { veri, setVeri, yukle };
}

/** Ad + parça satırları (oran %, neye göre, kaç gün) + toplam %100 denetimi. */
function VadeForm({ t, degis }: { t: Taslak; degis: (t: Taslak) => void }) {
  const toplam = t.parcalar.reduce((s, p) => s + (sayi(p.oranPct) || 0), 0);
  const tamam = Math.abs(toplam - 100) < 0.001;
  const parca = (n: number, alan: Partial<Taslak['parcalar'][number]>) =>
    degis({ ...t, parcalar: t.parcalar.map((p, i) => (i === n ? { ...p, ...alan } : p)) });
  const onizleme = taslaktan({ ...t, id: 'onizleme' });
  return (
    <div className="ccd-stack-sm">
      <div className="ccd-field">
        <label className="ccd-field-label" htmlFor="vade-ad">Listede görünecek ad<span className="ccd-required"> *</span></label>
        <input id="vade-ad" className="ccd-input" value={t.ad} maxLength={80} autoFocus
               onChange={(e) => degis({ ...t, ad: e.target.value })} placeholder="Örn. Sipariş + 20 gün" />
      </div>
      <div className="ccd-vade-parcalar">
        <div className="ccd-vade-parca ccd-vade-parca-baslik" aria-hidden="true">
          <span>Oran %</span><span>Neye göre</span><span>Kaç gün sonra</span><span />
        </div>
        {t.parcalar.map((p, n) => (
          <div key={n} className="ccd-vade-parca">
            <input className="ccd-input" type="number" inputMode="decimal" min={1} max={100} step="any" aria-label={`${n + 1}. parça oranı (%)`}
                   value={p.oranPct} onChange={(e) => parca(n, { oranPct: e.target.value })} />
            <select className="ccd-select" aria-label={`${n + 1}. parça neye göre`} value={p.taban}
                    onChange={(e) => parca(n, { taban: e.target.value as VadeTaban })}>
              {VADE_TABAN.map((b) => <option key={b} value={b}>{VADE_TABAN_LABEL[b]}</option>)}
            </select>
            {vadeGunlu(p.taban) ? (
              <input className="ccd-input" type="number" inputMode="numeric" min={0} max={730} step={1} aria-label={`${n + 1}. parça gün sayısı`}
                     value={p.gun} onChange={(e) => parca(n, { gun: e.target.value })} />
            ) : (
              <span className="ccd-meta">{p.taban === 'PESIN' ? 'sipariş günü' : 'sevke geçerken'}</span>
            )}
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={t.parcalar.length === 1}
                    title="Parçayı sil" aria-label={`${n + 1}. parçayı sil`}
                    onClick={() => degis({ ...t, parcalar: t.parcalar.filter((_, i) => i !== n) })}>×</button>
          </div>
        ))}
      </div>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <button type="button" className="ccd-btn ccd-btn-sm" disabled={t.parcalar.length >= 6}
                onClick={() => degis({ ...t, parcalar: [...t.parcalar, { oranPct: String(Math.max(0, Math.round((100 - toplam) * 100) / 100) || ''), taban: 'FATURA', gun: '30' }] })}>
          + Parça ekle
        </button>
        <span className={`ccd-vade-toplam${tamam ? '' : ' ccd-vade-toplam-hata'}`} role="status">
          Toplam %{String(Math.round(toplam * 100) / 100).replace('.', ',')}{tamam ? ' ✓' : ' — %100 olmalı'}
        </span>
      </div>
      {tamam && <div className="ccd-meta">Teklifte ve PDF'te şöyle yazar: <b>{vadeEtiket(onizleme.parcalar as VadeParca[])}</b></div>}
    </div>
  );
}

/**
 * Teklif (ya da sipariş) ekranından ayrılmadan yeni vade tanımı. Kaydedince
 * liste yenilenir ve yeni tanımın kimliği `eklendi` ile döner (seçili gelsin diye).
 */
export function VadeCekmece({ kapat, eklendi }: { kapat: () => void; eklendi: (yeni: VadeGorunum, liste: Yanit) => void }) {
  const [t, setT] = useState<Taslak>(bosTaslak);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    const yeni = taslaktan(t);
    const kusur = vadeSecenekHatasi(yeni);
    if (kusur) { setHata(kusur); return; }
    setHata(null); setBekliyor(true);
    try {
      // Liste her zaman TAZE okunur: başka biri bu arada tanım eklediyse ezilmesin.
      const guncel = await api<Yanit>('/api/vade-secenekleri');
      const r = await api<Yanit>('/api/vade-secenekleri', {
        method: 'PUT',
        body: JSON.stringify({ secenekler: [...guncel.secenekler.map(({ id, ad, parcalar }) => ({ id, ad, parcalar })), yeni] }),
      });
      eklendi(r.secenekler.find((s) => s.id === yeni.id)!, r);
    } catch (err) { setHata(err instanceof Error ? err.message : 'Vade tanımı kaydedilemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <div className="ccd-drawer-arka" onClick={() => !bekliyor && kapat()} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Yeni vade tanımla">
        <form className="ccd-stack" onSubmit={kaydet}>
          <div className="ccd-row-between">
            <div><div className="ccd-eyebrow">Tanımlar</div><h2 className="ccd-heading">Yeni vade tanımla</h2></div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} disabled={bekliyor}>Kapat</button>
          </div>
          <div className="ccd-meta">
            Tanım listeye eklenir ve bu kayıtta seçili gelir. Sonradan <b>Ayarlar → Vade seçenekleri</b>nden düzenlenir.
          </div>
          {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}
          <VadeForm t={t} degis={setT} />
          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : 'Tanımı kaydet ve seç'}</button>
        </form>
      </aside>
    </div>
  );
}

/** Ayarlar ekranındaki "Vade seçenekleri" bölümü: liste, ekle / düzenle / sil, varsayılanlara dön. */
export function VadeAyar() {
  const { can } = useAuth();
  const yazar = can('settings.write');
  const { veri, setVeri } = useVadeler();
  const [t, setT] = useState<Taslak | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  // `/ayarlar#vade` bağlantısı bu bölüme kaydırır.
  useEffect(() => hashaKaydir('vade'), []);

  async function yaz(govde: { secenekler: VadeSecenegi[] } | { varsayilan: true }, mesaj: string) {
    setHata(null); setBilgi(null); setBekliyor(true);
    try {
      setVeri(await api<Yanit>('/api/vade-secenekleri', { method: 'PUT', body: JSON.stringify(govde) }));
      setBilgi(mesaj); setT(null);
    } catch (e) { setHata(e instanceof Error ? e.message : 'Kaydedilemedi'); }
    finally { setBekliyor(false); }
  }
  const sade = (l: VadeGorunum[]) => l.map(({ id, ad, parcalar }) => ({ id, ad, parcalar }));

  function kaydet(e: React.FormEvent) {
    e.preventDefault();
    if (!veri || !t) return;
    const yeni = taslaktan(t);
    const kusur = vadeSecenekHatasi(yeni);
    if (kusur) { setHata(kusur); return; }
    const liste = sade(veri.secenekler);
    void yaz({ secenekler: t.id ? liste.map((s) => (s.id === t.id ? yeni : s)) : [...liste, yeni] }, `"${yeni.ad}" kaydedildi.`);
  }

  return (
    <div className="ccd-card ccd-stack-sm" id="vade">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="ccd-fieldset-title">Vade seçenekleri</div>
          <div className="ccd-meta">
            Teklifte vade elle yazılmaz, bu listeden seçilir. Sipariş açılınca seçilen vade ödeme planına döner;
            vadesi gelen ödeme Tahsilat'a düşer ve hatırlatılır. Eski tekliflerdeki vade, tanım değişse de aynı kalır.
          </div>
        </div>
        {veri && <span className="ccd-badge">{veri.varsayilan ? 'varsayılan liste' : 'özelleştirilmiş'}</span>}
      </div>
      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon">✓</span><span>{bilgi}</span></div>}
      {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}

      {!veri ? <div className="ccd-inline-note">Yükleniyor…</div> : (
        <div className="ccd-vade-liste">
          {veri.secenekler.map((s) => (
            <div key={s.id} className="ccd-vade-satir">
              <div style={{ minWidth: 0 }}>
                <b>{s.ad}</b>
                {s.etiket !== s.ad && <div className="ccd-meta">{s.etiket}</div>}
              </div>
              {yazar && (
                <span className="ccd-row" style={{ gap: 6 }}>
                  <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={bekliyor}
                          onClick={() => { setHata(null); setBilgi(null); setT({ id: s.id, ad: s.ad, parcalar: s.parcalar.map((p) => ({ oranPct: String(p.oranPct), taban: p.taban, gun: String(p.gun) })) }); }}>Düzenle</button>
                  <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" style={{ color: 'var(--ccd-danger)' }} disabled={bekliyor || veri.secenekler.length === 1}
                          onClick={() => { if (window.confirm(`"${s.ad}" listeden silinsin mi? Bu vadeyle hazırlanmış teklif ve siparişler etkilenmez.`)) void yaz({ secenekler: sade(veri.secenekler).filter((x) => x.id !== s.id) }, `"${s.ad}" silindi.`); }}>Sil</button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {yazar && veri && (t ? (
        <form className="ccd-vade-form ccd-stack-sm" onSubmit={kaydet}>
          <div className="ccd-fieldset-title">{t.id ? 'Vadeyi düzenle' : 'Yeni vade'}</div>
          <VadeForm t={t} degis={setT} />
          <div className="ccd-row" style={{ gap: 8 }}>
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : 'Kaydet'}</button>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => { setT(null); setHata(null); }}>Vazgeç</button>
          </div>
        </form>
      ) : (
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => { setHata(null); setBilgi(null); setT(bosTaslak()); }}>+ Vade ekle</button>
          {!veri.varsayilan && (
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={bekliyor}
                    onClick={() => { if (window.confirm('Liste varsayılan seçeneklere dönsün mü? Eklediğiniz ve değiştirdiğiniz tanımlar silinir.')) void yaz({ varsayilan: true }, 'Varsayılan vade listesine dönüldü.'); }}>
              Varsayılanlara dön
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
